import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Context/auth';
import MenuLateral from '../../Components/MenuPrincipal/MenuLateral';
import '../financeiro.css';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import Swal from 'sweetalert2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const DashboardFinanceiro = () => {
    const { currentUser } = useAuth();

    // Default data structure to avoid initial undefined errors before fetch
    const [data, setData] = useState({
        saldoPrevisto: 0,
        lucroLiquido: 0,
        margemLucro: 0,
        recebidoMes: 0,
        aReceberMes: 0,
        previsaoReceitas: 0,
        pagoMes: 0,
        aPagarMes: 0,
        previsaoDespesas: 0,
        totalReceberGeral: 0,
        totalPagarGeral: 0,
        chartData: []
    });

    const [loading, setLoading] = useState(true);
    const [filterPeriod, setFilterPeriod] = useState('thisMonth');

    const fetchData = React.useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const sessionId = localStorage.getItem('sessionId');

            const API_URL = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${API_URL}/api/financeiro/dashboard?period=${filterPeriod}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-session-id': sessionId
                }
            });

            if (response.ok) {
                const result = await response.json();
                console.log("Dashboard Data Received:", result); // Debug no console do navegador
                setData(result);
            } else {
                const errorText = await response.text();
                console.error("Erro ao buscar dados dashboard:", response.status, errorText);
                Swal.fire('Erro', 'Não foi possível carregar os dados do dashboard.', 'error');
            }
        } catch (error) {
            console.error("Erro fetch dashboard:", error);
            Swal.fire('Erro', 'Falha na comunicação com o servidor.', 'error');
        } finally {
            setLoading(false);
        }
    }, [currentUser, filterPeriod]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getPeriodLabel = () => {
        switch (filterPeriod) {
            case 'hoje': return 'Hoje';
            case 'thisWeek': return 'Esta Semana';
            case 'thisMonth': return 'Este Mês';
            case 'thisYear': return 'Este Ano';
            default: return 'Este Mês';
        }
    };

    // Configuração do Gráfico
    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: `Fluxo de Caixa Diário (${getPeriodLabel()})`,
            },
        },
        scales: {
            y: {
                beginAtZero: true
            }
        }
    };

    const chartData = {
        labels: data?.chartData?.map(d => d.data_formatada) || [],
        datasets: [
            {
                label: 'Receitas',
                data: data?.chartData?.map(d => d.receitas) || [],
                backgroundColor: 'rgba(25, 135, 84, 0.7)',
                borderColor: 'rgba(25, 135, 84, 1)',
                borderWidth: 1
            },
            {
                label: 'Despesas',
                data: data?.chartData?.map(d => d.despesas) || [],
                backgroundColor: 'rgba(220, 53, 69, 0.7)',
                borderColor: 'rgba(220, 53, 69, 1)',
                borderWidth: 1
            },
        ],
    };

    const formatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
    const percentFormatter = new Intl.NumberFormat('pt-BR', { style: 'percent', minimumFractionDigits: 2 });

    const safeVal = (val) => val || 0; // Helper extra para renderização segura

    if (loading) {
        return (
            <div style={{ display: 'flex', paddingTop: 0 }}>
                <MenuLateral />
                <div className="content-with-sidebar d-flex justify-content-center align-items-center" style={{ width: '100%', height: '100vh' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />
            <div className="content-with-sidebar">
                <div className="financeiro-header">
                    <h1 className="financeiro-title"><i className="fas fa-chart-pie me-2"></i>Dashboard Financeiro</h1>
                    <div className="d-flex gap-3 align-items-center">
                        <div className="d-flex align-items-center">
                            <label className="me-2 fw-bold text-secondary">Período:</label>
                            <div style={{ width: '180px' }}>
                                <select
                                    className="form-select"
                                    value={filterPeriod}
                                    onChange={(e) => setFilterPeriod(e.target.value)}
                                >
                                    <option value="hoje">Hoje</option>
                                    <option value="thisWeek">Esta Semana</option>
                                    <option value="thisMonth">Este Mês</option>
                                    <option value="thisYear">Este Ano</option>
                                </select>
                            </div>
                        </div>
                        <div className="d-flex gap-2 align-items-center">
                            <button className="btn btn-outline-secondary btn-sm" onClick={fetchData} title="Atualizar">
                                <i className="fas fa-sync-alt"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="financeiro-content">

                    {/* Linha 1: Resumo Executivo (Saldo, Lucro, Margem) */}
                    <div className="row g-3 mb-4">
                        {/* Saldo Final Previsto */}
                        <div className="col-lg-4 col-md-6">
                            <div className={`card h-100 shadow-sm border-0 border-start border-4 ${safeVal(data.saldoPrevisto) >= 0 ? 'border-success' : 'border-danger'}`}>
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h6 className="text-secondary text-uppercase fw-bold mb-1">Previsão Saldo Final</h6>
                                            <h3 className={`fw-bold mb-0 ${safeVal(data.saldoPrevisto) >= 0 ? 'text-success' : 'text-danger'}`}>
                                                {formatter.format(safeVal(data.saldoPrevisto))}
                                            </h3>
                                        </div>
                                        <div className={`p-2 rounded-circle ${safeVal(data.saldoPrevisto) >= 0 ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'}`}>
                                            <i className="fas fa-wallet fa-lg"></i>
                                        </div>
                                    </div>
                                    <small className="text-muted mt-2 d-block">Considera realizado + agendado no período</small>
                                </div>
                            </div>
                        </div>

                        {/* Lucro Líquido Realizado */}
                        <div className="col-lg-4 col-md-6">
                            <div className="card h-100 shadow-sm border-0 border-start border-4 border-info">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h6 className="text-secondary text-uppercase fw-bold mb-1">Lucro Líquido (Realizado)</h6>
                                            <h3 className="fw-bold text-dark mb-0">{formatter.format(safeVal(data.lucroLiquido))}</h3>
                                        </div>
                                        <div className="p-2 rounded-circle bg-info bg-opacity-10 text-info">
                                            <i className="fas fa-chart-line fa-lg"></i>
                                        </div>
                                    </div>
                                    <small className="text-muted mt-2 d-block">Receitas Realizadas - Despesas Realizadas</small>
                                </div>
                            </div>
                        </div>

                        {/* Margem de Lucro */}
                        <div className="col-lg-4 col-md-12">
                            <div className="card h-100 shadow-sm border-0 border-start border-4 border-warning">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h6 className="text-secondary text-uppercase fw-bold mb-1">Margem de Lucro</h6>
                                            <h3 className="fw-bold text-dark mb-0">{percentFormatter.format(safeVal(data.margemLucro) / 100)}</h3>
                                        </div>
                                        <div className="p-2 rounded-circle bg-warning bg-opacity-10 text-warning">
                                            <i className="fas fa-percent fa-lg"></i>
                                        </div>
                                    </div>
                                    <small className="text-muted mt-2 d-block">Sobre Receita Realizada</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Linha 2: Detalhamento Receitas e Despesas */}
                    <div className="row g-3 mb-4">
                        {/* Receitas */}
                        <div className="col-md-6">
                            <div className="card shadow-sm border-0 h-100">
                                <div className="card-header bg-success bg-opacity-10 border-0">
                                    <h6 className="mb-0 fw-bold text-success"><i className="fas fa-arrow-up me-2"></i>Receitas</h6>
                                </div>
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>Realizado:</span>
                                        <span className="fw-bold text-success">{formatter.format(safeVal(data.recebidoMes))}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>A Receber (Venc. Período):</span>
                                        <span className="fw-bold text-secondary">{formatter.format(safeVal(data.aReceberMes))}</span>
                                    </div>
                                    <hr />
                                    <div className="d-flex justify-content-between">
                                        <span className="fw-bold">Previsão Total:</span>
                                        <span className="fw-bold text-dark">{formatter.format(safeVal(data.previsaoReceitas))}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Despesas */}
                        <div className="col-md-6">
                            <div className="card shadow-sm border-0 h-100">
                                <div className="card-header bg-danger bg-opacity-10 border-0">
                                    <h6 className="mb-0 fw-bold text-danger"><i className="fas fa-arrow-down me-2"></i>Despesas</h6>
                                </div>
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>Realizado:</span>
                                        <span className="fw-bold text-danger">{formatter.format(safeVal(data.pagoMes))}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>A Pagar (Venc. Período):</span>
                                        <span className="fw-bold text-secondary">{formatter.format(safeVal(data.aPagarMes))}</span>
                                    </div>
                                    <hr />
                                    <div className="d-flex justify-content-between">
                                        <span className="fw-bold">Previsão Total:</span>
                                        <span className="fw-bold text-dark">{formatter.format(safeVal(data.previsaoDespesas))}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Linha 3: Gráfico e Totais Gerais */}
                    <div className="row g-3">
                        <div className="col-lg-8">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div style={{ position: 'relative', height: '350px', width: '100%' }}>
                                        <Bar options={{ maintainAspectRatio: false, ...chartOptions }} data={chartData} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4">
                            <div className="card border-0 shadow-sm h-100 bg-light">
                                <div className="card-body">
                                    <h5 className="card-title text-secondary mb-4">Pendências Gerais (Estoque)</h5>

                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between align-items-end mb-1">
                                            <label className="small text-uppercase text-muted fw-bold mb-0">Total A Receber (Geral)</label>
                                            <small className="text-danger fw-bold" title="Total Vencido">
                                                <i className="fas fa-exclamation-circle me-1"></i>
                                                {formatter.format(safeVal(data.totalReceberVencido))} Vencido
                                            </small>
                                        </div>
                                        <h4 className="fw-bold text-dark">{formatter.format(safeVal(data.totalReceberGeral))}</h4>
                                        <div className="progress" style={{ height: '6px' }}>
                                            <div className="progress-bar bg-warning" role="progressbar" style={{ width: '100%' }}></div>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between align-items-end mb-1">
                                            <label className="small text-uppercase text-muted fw-bold mb-0">Total A Pagar (Geral)</label>
                                            <small className="text-danger fw-bold" title="Total Vencido">
                                                <i className="fas fa-exclamation-circle me-1"></i>
                                                {formatter.format(safeVal(data.totalPagarVencido))} Vencido
                                            </small>
                                        </div>
                                        <h4 className="fw-bold text-dark">{formatter.format(safeVal(data.totalPagarGeral))}</h4>
                                        <div className="progress" style={{ height: '6px' }}>
                                            <div className="progress-bar bg-danger" role="progressbar" style={{ width: '100%' }}></div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardFinanceiro;
